import { type BusinessModel, headlineOutcome } from '../analytics/business-model';

export const MEASUREMENT_OUTCOMES = [
  'leads',
  'purchases',
  'traffic',
  'awareness',
  'engagement',
] as const;

export type MeasurementOutcome = (typeof MEASUREMENT_OUTCOMES)[number];

export type MeasurementGoal = 'leads' | 'sales' | 'traffic' | 'awareness' | 'engagement';

const GOAL_OF: Record<MeasurementOutcome, MeasurementGoal> = {
  leads: 'leads',
  purchases: 'sales',
  traffic: 'traffic',
  awareness: 'awareness',
  engagement: 'engagement',
};

export function parseMeasurementOutcome(value: unknown): MeasurementOutcome | null {
  return typeof value === 'string' && (MEASUREMENT_OUTCOMES as readonly string[]).includes(value)
    ? (value as MeasurementOutcome)
    : null;
}

/** The generic paid-builder goal a brief's measured outcome maps to. */
export function measurementOutcomeGoal(outcome: MeasurementOutcome): MeasurementGoal {
  return GOAL_OF[outcome];
}

/** Whether a chat `setupGoal` names the same result as a brief's measured outcome. */
export function setupGoalAgreesWithOutcome(
  setupGoal: string,
  outcome: MeasurementOutcome,
): boolean {
  const goal = setupGoal === 'website-traffic' ? 'traffic' : setupGoal;
  return GOAL_OF[outcome] === goal;
}

/** Outcomes a campaign can only report through a conversion rule, pixel or tag. */
export function outcomeNeedsConversionTracking(outcome: MeasurementOutcome | null): boolean {
  return outcome === 'leads' || outcome === 'purchases';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The outcome a builder start carries, read defensively from the two
 * `unknown`-typed handoffs (`ShellStartContext.briefHandoff` and
 * `.authoringHandoff`). A brief handoff wins over a chat hint.
 */
export function measurementOutcomeFromHandoffs(
  briefHandoff: unknown,
  authoringHandoff: unknown,
): MeasurementOutcome | null {
  if (isRecord(briefHandoff)) {
    const fromBrief = parseMeasurementOutcome(briefHandoff.measurementOutcome);
    if (fromBrief) return fromBrief;
  }
  if (!isRecord(authoringHandoff)) return null;
  if (authoringHandoff.source === 'brief') {
    return isRecord(authoringHandoff.briefHandoff)
      ? parseMeasurementOutcome(authoringHandoff.briefHandoff.measurementOutcome)
      : null;
  }
  return parseMeasurementOutcome(authoringHandoff.measurementOutcomeHint);
}

const TEXT_OVERRIDES: ReadonlyArray<{ outcome: MeasurementOutcome; pattern: RegExp }> = [
  { outcome: 'awareness', pattern: /\bawareness\b/i },
  { outcome: 'traffic', pattern: /\btraffic\b/i },
  { outcome: 'purchases', pattern: /\b(sales|purchases)\b/i },
  { outcome: 'leads', pattern: /\bleads?\b/i },
];

function outcomeFromText(text: string | null | undefined): MeasurementOutcome | null {
  if (!text) return null;
  const hits = TEXT_OVERRIDES.filter((entry) => entry.pattern.test(text)).map(
    (entry) => entry.outcome,
  );
  return hits.length === 1 ? hits[0]! : null;
}

/**
 * The outcome a Brief shows as its computed default (the Measurement row's third rung, after a
 * human decision and the stored default). The workspace's business model decides unless the
 * brief's own words name exactly one outcome (success measure first, then goal). Two conflicting
 * words, or none, mean no override. One definition, read by the Measurement row and the draft
 * engine so the builder cannot disagree with the brief about what it measures.
 */
export function computedMeasurementOutcome(input: {
  businessModel: BusinessModel | null;
  successMeasureText?: string | null;
  goalText?: string | null;
}): MeasurementOutcome {
  const fromText = outcomeFromText(input.successMeasureText) ?? outcomeFromText(input.goalText);
  if (fromText) return fromText;
  const headline = headlineOutcome(input.businessModel);
  if (headline === 'leads') return 'leads';
  if (headline === 'audience') return 'engagement';
  return 'purchases';
}

/**
 * The outcome a Brief measures itself by, in the order the Brief page shows it: the human
 * decision, the plan stored when the Brief was generated, then the computed default. One
 * definition for the Measurement row and for a builder that opens from the Brief, so the
 * builder cannot start on an objective the Brief disagrees with (ITC Tax run 8: a Brief
 * measured in leads opened the LinkedIn builder with no objective chosen).
 */
export function briefOutcomeForBuilder(input: {
  decisionOutcome?: unknown;
  storedOutcome?: unknown;
  businessModel: BusinessModel | null;
  successMeasureText?: string | null;
  goalText?: string | null;
}): MeasurementOutcome {
  return (
    parseMeasurementOutcome(input.decisionOutcome) ??
    parseMeasurementOutcome(input.storedOutcome) ??
    computedMeasurementOutcome({
      businessModel: input.businessModel,
      successMeasureText: input.successMeasureText ?? null,
      goalText: input.goalText ?? null,
    })
  );
}

/**
 * The LinkedIn objective a measured outcome starts the builder on. Purchases has none:
 * LinkedIn here has no sales tracking, so the person chooses.
 */
export function linkedInObjectiveForOutcome(
  outcome: MeasurementOutcome,
): 'LEAD_GENERATION' | 'WEBSITE_VISIT' | 'BRAND_AWARENESS' | 'ENGAGEMENT' | null {
  switch (outcome) {
    case 'leads':
      return 'LEAD_GENERATION';
    case 'traffic':
      return 'WEBSITE_VISIT';
    case 'awareness':
      return 'BRAND_AWARENESS';
    case 'engagement':
      return 'ENGAGEMENT';
    default:
      return null;
  }
}
